/**
 * The exploration subsystem's working state (plan R18): the live config, the new-game knowledge (chart novelty compares
 * against it), the current system's chart multipliers, the combat watch, the scan latch, per-visit POI flags and the
 * notices the HUD can show. Also the shared helpers every part uses (chart accrual, POI reveal, notices). Per-visit
 * arrays are rebuilt on system entry (a rare event); per-tick readers only index them.
 */
import { logger } from '../core/logger'
import type { PoiSite, SystemLayout } from '../galaxy/layout/types'
import { emitReach, REV } from '../reach/events'
import type { ReachControls } from '../reach/controls'
import type { ReachCore } from '../reach/runtime/types'
import { accrueChart, discoverPoi, deltaOf, type KnownLevel } from '../reach/state'
import type { ExploreConfig } from './config'
import { poiChartValue, type ChartMultipliers } from './valuation'

/** A short message for the HUD's toasts (flavour text, reward details, refusals). */
export interface ExplorationNotice {
  readonly seq: number
  readonly text: string
}

/** How many notices are kept (oldest dropped first). */
export const NOTICE_CAPACITY = 16

export interface ExplorationContext {
  readonly cfg: ExploreConfig
  readonly controls: ReachControls
  /** The new-game knowledge of the current galaxy (set at `init`). */
  initial: Readonly<Record<string, KnownLevel>>
  /** The current system's chart multipliers. */
  readonly mult: ChartMultipliers
  /** Combat ring cursor and the `sim.time` of the last hit on the player. */
  hitCursor: number
  lastHitAt: number
  /** The scan needs the key released after a ping. */
  latched: boolean
  cooldownUntil: number
  /** POI indices of the current system's anomalies and hidden bases. */
  anomalies: Int32Array
  bases: Int32Array
  /** Per POI index, this visit: looted/surveyed, base cleared, base alerted, defender group id (0 = none). */
  looted: Uint8Array
  cleared: Uint8Array
  alerted: Uint8Array
  groups: Int32Array
  readonly notices: ExplorationNotice[]
  noticeSeq: number
}

export function createExplorationContext(cfg: ExploreConfig, controls: ReachControls): ExplorationContext {
  return {
    cfg,
    controls,
    initial: {},
    mult: { novelty: 1, first: 1 },
    hitCursor: 0,
    lastHitAt: Number.NEGATIVE_INFINITY,
    latched: false,
    cooldownUntil: 0,
    anomalies: new Int32Array(0),
    bases: new Int32Array(0),
    looted: new Uint8Array(0),
    cleared: new Uint8Array(0),
    alerted: new Uint8Array(0),
    groups: new Int32Array(0),
    notices: [],
    noticeSeq: 0,
  }
}

/** Rebuilds the per-visit POI tables for `layout` from the saved deltas. Rare (system entry). */
export function enterVisit(x: ExplorationContext, core: ReachCore, layout: SystemLayout): void {
  const n = layout.pois.length
  const delta = deltaOf(core.state, layout.systemId)
  const anomalies: number[] = []
  const bases: number[] = []
  x.looted = new Uint8Array(n)
  x.cleared = new Uint8Array(n)
  x.alerted = new Uint8Array(n)
  x.groups = new Int32Array(n)
  for (let i = 0; i < n; i++) {
    const poi = layout.pois[i]
    if (delta.lootedPois.includes(poi.id)) x.looted[i] = 1
    if (delta.clearedBases.includes(poi.id)) x.cleared[i] = 1
    if (poi.kind === 'anomaly') anomalies.push(i)
    else if (poi.kind === 'hidden-base') bases.push(i)
  }
  x.anomalies = Int32Array.from(anomalies)
  x.bases = Int32Array.from(bases)
}

/** Clears the per-visit tables (the session despawned every group with the local space). */
export function leaveVisit(x: ExplorationContext): void {
  x.anomalies = new Int32Array(0)
  x.bases = new Int32Array(0)
  x.looted = new Uint8Array(0)
  x.cleared = new Uint8Array(0)
  x.alerted = new Uint8Array(0)
  x.groups = new Int32Array(0)
  x.latched = false
}

/** Adds chart value to the system's unsold ledger and emits `REV.chartValue`. Ignores 0. */
export function accrue(core: ReachCore, layout: SystemLayout, amount: number): void {
  if (!(amount > 0)) return
  accrueChart(core.state, layout.systemId, amount)
  emitReach(core.events, REV.chartValue, layout.systemIndex, 0, 0, 0, 0, amount)
}

/** Discovers a POI (first time only): `REV.poiRevealed` and its chart richness. True when it was new. */
export function revealPoi(x: ExplorationContext, core: ReachCore, layout: SystemLayout, poi: PoiSite): boolean {
  if (!discoverPoi(core.state, layout.systemId, poi.id)) return false
  emitReach(core.events, REV.poiRevealed, layout.systemIndex, poi.index, poi.position[0], poi.position[1], poi.position[2], 0)
  accrue(core, layout, poiChartValue(layout.region, poi.hidden, x.mult, x.cfg))
  return true
}

/** Queues a HUD notice (and logs it). */
export function notice(x: ExplorationContext, text: string): void {
  x.noticeSeq++
  x.notices.push({ seq: x.noticeSeq, text })
  if (x.notices.length > NOTICE_CAPACITY) x.notices.splice(0, x.notices.length - NOTICE_CAPACITY)
  logger.info(`exploration: ${text}`)
}
